import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import { allUsers } from "@/mocks/users";

interface DispatchRecord {
  id: string;
  userId: string;
  userName: string;
  deviceId: string;
  priceScheme: string;
  tier1Price: number;
  tier2Price: number;
  tier3Price: number;
  tier1Limit: number;
  tier2Limit: number;
  dispatchTime: string;
  status: "待下发" | "下发中" | "已确认" | "下发失败";
  retryCount: number;
  confirmTime?: string;
}

const initialRecords: DispatchRecord[] = [
  { id: "DP001", userId: "R001", userName: "张建国", deviceId: "IOT-R001-GM", priceScheme: "居民阶梯2026", tier1Price: 2.93, tier2Price: 3.22, tier3Price: 4.00, tier1Limit: 240, tier2Limit: 480, dispatchTime: "2026-04-10 10:00:00", status: "已确认", retryCount: 0, confirmTime: "2026-04-10 10:00:08" },
  { id: "DP002", userId: "R002", userName: "李秀梅", deviceId: "IOT-R002-GM", priceScheme: "居民阶梯2026", tier1Price: 2.93, tier2Price: 3.22, tier3Price: 4.00, tier1Limit: 240, tier2Limit: 480, dispatchTime: "2026-04-10 10:00:00", status: "下发失败", retryCount: 3, },
  { id: "DP003", userId: "C001", userName: "老北京涮肉馆", deviceId: "IOT-C001-FM", priceScheme: "小餐饮2026", tier1Price: 3.50, tier2Price: 3.80, tier3Price: 4.50, tier1Limit: 5000, tier2Limit: 15000, dispatchTime: "2026-04-10 10:00:00", status: "已确认", retryCount: 0, confirmTime: "2026-04-10 10:00:12" },
  { id: "DP004", userId: "C002", userName: "北京华联超市", deviceId: "IOT-C002-FM", priceScheme: "大型企事业2026", tier1Price: 3.20, tier2Price: 3.60, tier3Price: 4.20, tier1Limit: 50000, tier2Limit: 150000, dispatchTime: "2026-04-10 10:00:00", status: "待下发", retryCount: 0 },
  { id: "DP005", userId: "R003", userName: "王大明", deviceId: "IOT-R003-GM", priceScheme: "居民阶梯2026", tier1Price: 2.93, tier2Price: 3.22, tier3Price: 4.00, tier1Limit: 240, tier2Limit: 480, dispatchTime: "2026-04-10 10:00:00", status: "已确认", retryCount: 1, confirmTime: "2026-04-10 10:00:35" },
];

const statusColors: Record<string, string> = {
  待下发: "bg-gray-100 text-gray-500",
  下发中: "bg-blue-50 text-blue-600",
  已确认: "bg-emerald-50 text-emerald-600",
  下发失败: "bg-red-50 text-red-600",
};

const statusIcons: Record<string, string> = {
  待下发: "ri-time-line",
  下发中: "ri-loader-4-line",
  已确认: "ri-checkbox-circle-line",
  下发失败: "ri-close-circle-line",
};

export default function PriceDispatchPage() {
  const [records, setRecords] = useState<DispatchRecord[]>(initialRecords);
  const [dispatching, setDispatching] = useState<string | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchTarget, setBatchTarget] = useState("全部用户");
  const [batchScheme, setBatchScheme] = useState("居民阶梯2026");
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchDone, setBatchDone] = useState(false);

  const handleRetry = (id: string) => {
    setDispatching(id);
    setRecords(prev => prev.map(r => r.id === id ? { ...r, status: "下发中" } : r));
    setTimeout(() => {
      setRecords(prev => prev.map(r =>
        r.id === id
          ? { ...r, status: "已确认", confirmTime: new Date().toLocaleString("zh-CN"), retryCount: r.retryCount + 1 }
          : r
      ));
      setDispatching(null);
    }, 2000);
  };

  const handleBatchDispatch = () => {
    setBatchProgress(0);
    setBatchDone(false);
    const interval = setInterval(() => {
      setBatchProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          setBatchDone(true);
          setRecords(prev => prev.map(r => ({ ...r, status: "已确认" as const, confirmTime: new Date().toLocaleString("zh-CN") })));
          return 100;
        }
        return p + 10;
      });
    }, 200);
  };

  const confirmed = records.filter(r => r.status === "已确认").length;
  const failed = records.filter(r => r.status === "下发失败").length;
  const pending = records.filter(r => r.status === "待下发").length;

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">阶梯气价下发管理</h2>
            <p className="text-xs text-gray-400 mt-0.5">向IoT设备下发最新阶梯气价，燃气表收到后立即执行新计费标准</p>
          </div>
          <button
            onClick={() => setShowBatchModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-line"></i>批量下发气价
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "下发总数", value: records.length, color: "text-gray-700", bg: "bg-gray-50" },
            { label: "已确认", value: confirmed, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "待下发", value: pending, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "下发失败", value: failed, color: "text-red-600", bg: "bg-red-50" },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-4 flex items-center justify-between`}>
              <div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Dispatch Records */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">下发记录明细</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  {["用户", "设备编号", "价格方案", "一档(元/m³)", "二档(元/m³)", "三档(元/m³)", "下发时间", "状态", "重试次数", "操作"].map(h => (
                    <th key={h} className="text-left py-2.5 px-3 text-gray-400 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-3">
                      <p className="font-medium text-gray-800">{r.userName}</p>
                      <p className="text-gray-400">{r.userId}</p>
                    </td>
                    <td className="py-3 px-3 font-mono text-gray-500">{r.deviceId}</td>
                    <td className="py-3 px-3 text-gray-600">{r.priceScheme}</td>
                    <td className="py-3 px-3 font-semibold text-emerald-600">¥{r.tier1Price}</td>
                    <td className="py-3 px-3 font-semibold text-amber-600">¥{r.tier2Price}</td>
                    <td className="py-3 px-3 font-semibold text-red-500">¥{r.tier3Price}</td>
                    <td className="py-3 px-3 text-gray-400 whitespace-nowrap">{r.dispatchTime}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1.5">
                        <i className={`${statusIcons[r.status]} text-sm ${r.status === "下发中" ? "animate-spin" : ""} ${
                          r.status === "已确认" ? "text-emerald-500" : r.status === "下发失败" ? "text-red-500" : "text-gray-400"
                        }`}></i>
                        <span className={`px-2 py-0.5 rounded-full ${statusColors[r.status]}`}>{r.status}</span>
                      </div>
                      {r.confirmTime && <p className="text-gray-300 mt-0.5">{r.confirmTime}</p>}
                    </td>
                    <td className="py-3 px-3">
                      <span className={r.retryCount > 0 ? "text-amber-600 font-medium" : "text-gray-400"}>{r.retryCount}次</span>
                    </td>
                    <td className="py-3 px-3">
                      {r.status === "下发失败" && (
                        <button
                          onClick={() => handleRetry(r.id)}
                          disabled={dispatching === r.id}
                          className="px-3 py-1.5 text-xs bg-[#E8531A] text-white rounded-lg hover:bg-[#d44a15] disabled:opacity-50 cursor-pointer whitespace-nowrap"
                        >
                          {dispatching === r.id ? "重试中..." : "重新下发"}
                        </button>
                      )}
                      {r.status === "待下发" && (
                        <button
                          onClick={() => handleRetry(r.id)}
                          className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                        >
                          立即下发
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* IoT Flow Diagram */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">IoT下发流程说明</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { step: "1", label: "后台设置气价", icon: "ri-settings-3-line", color: "bg-orange-100 text-[#E8531A]" },
              { step: "→", label: "", icon: "", color: "" },
              { step: "2", label: "生成下发指令", icon: "ri-file-code-line", color: "bg-blue-100 text-blue-600" },
              { step: "→", label: "", icon: "", color: "" },
              { step: "3", label: "IoT平台推送", icon: "ri-wifi-line", color: "bg-violet-100 text-violet-600" },
              { step: "→", label: "", icon: "", color: "" },
              { step: "4", label: "燃气表接收", icon: "ri-cpu-line", color: "bg-emerald-100 text-emerald-600" },
              { step: "→", label: "", icon: "", color: "" },
              { step: "5", label: "回传确认", icon: "ri-checkbox-circle-line", color: "bg-green-100 text-green-600" },
            ].map((item, i) => (
              item.step === "→" ? (
                <div key={i} className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-gray-300 text-lg"></i>
                </div>
              ) : (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${item.color}`}>
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <span className="text-xs text-gray-500 text-center whitespace-nowrap">{item.label}</span>
                </div>
              )
            ))}
          </div>
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
            <p className="text-xs text-amber-700">
              <i className="ri-information-line mr-1"></i>
              <strong>自动重试机制：</strong>下发失败后系统自动重试，最多重试5次。超过5次失败后系统告警提示，需人工处理。燃气表收到新气价后立即执行新计费标准，回传确认成功后不再重复下发。
            </p>
          </div>
        </div>
      </div>

      {/* Batch Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[480px] max-w-full mx-4">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold text-gray-900">批量下发阶梯气价</h3>
              <button onClick={() => { setShowBatchModal(false); setBatchDone(false); setBatchProgress(0); }} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {!batchDone ? (
              <>
                <div className="space-y-3 mb-5">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">下发范围</label>
                    <select value={batchTarget} onChange={e => setBatchTarget(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none">
                      <option>全部用户</option>
                      <option>居民用户</option>
                      <option>工商业用户</option>
                      <option>小餐饮业</option>
                      <option>大型企事业</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">价格方案</label>
                    <select value={batchScheme} onChange={e => setBatchScheme(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none">
                      <option>居民阶梯2026</option>
                      <option>小餐饮2026</option>
                      <option>大型企事业2026</option>
                      <option>特殊行业2026</option>
                    </select>
                  </div>
                </div>
                {batchProgress > 0 && batchProgress < 100 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>下发进度</span><span>{batchProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#E8531A] rounded-full transition-all duration-200" style={{ width: `${batchProgress}%` }}></div>
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={() => setShowBatchModal(false)} className="flex-1 py-2.5 border border-gray-200 text-sm text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                  <button onClick={handleBatchDispatch} disabled={batchProgress > 0 && batchProgress < 100} className="flex-1 py-2.5 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] disabled:opacity-50 cursor-pointer whitespace-nowrap">
                    {batchProgress > 0 && batchProgress < 100 ? "下发中..." : "确认下发"}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-checkbox-circle-line text-emerald-600 text-3xl"></i>
                </div>
                <h4 className="text-base font-bold text-gray-900 mb-1">批量下发完成！</h4>
                <p className="text-xs text-gray-400">已向 {records.length} 台设备成功下发新阶梯气价</p>
                <button onClick={() => { setShowBatchModal(false); setBatchDone(false); setBatchProgress(0); }} className="mt-4 px-6 py-2 bg-[#E8531A] text-white text-sm rounded-lg cursor-pointer whitespace-nowrap">关闭</button>
              </div>
            )}
          </div>
        </div>
      )}
    </MainLayout>
  );
}
